/**
 * Guards browser navigation away from unauthenticated Supabase API endpoints.
 * Prevents the production JSON error page:
 *   {"message":"No API key found in request",...}
 */

export const UNSAFE_NAVIGATION_ERROR =
  'Unable to open this file. The storage link is invalid or has expired.';

export interface SafeOpenResult {
  ok: boolean;
  url?: string;
  error?: string;
}

/**
 * True when a URL must never be opened/navigated by the browser.
 * Rejects PostgREST, Auth, and unsigned Storage object URLs.
 */
export function isUnsafeSupabaseBrowserUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return true;
  const trimmed = url.trim();
  if (!trimmed) return true;
  if (/\bundefined\b/i.test(trimmed) || /\bnull\b/i.test(trimmed)) return true;

  const lower = trimmed.toLowerCase();
  if (lower.includes('/rest/v1/')) return true;
  if (lower.includes('/auth/v1/')) return true;
  if (lower.includes('/functions/v1/')) return true;

  // Storage: only allow signed object URLs (contain /object/sign/ and a token).
  if (lower.includes('/storage/v1/object/')) {
    const isSigned = lower.includes('/object/sign/');
    const hasToken = /[?&]token=/.test(trimmed) || trimmed.includes('/object/sign/');
    if (!isSigned || !hasToken) return true;
  }

  return false;
}

export function isSafeBrowserOpenUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (isUnsafeSupabaseBrowserUrl(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Open a URL in a new tab only after validation.
 * Never navigates to /rest/v1/, /auth/v1/, or unsigned storage URLs.
 */
export function safeOpenInNewTab(
  url: string | null | undefined,
  context?: { source?: string; fileId?: string | null; bucket?: string | null; path?: string | null }
): SafeOpenResult {
  if (!isSafeBrowserOpenUrl(url)) {
    // eslint-disable-next-line no-console
    console.warn('[safeOpenInNewTab] blocked unsafe URL', {
      source: context?.source ?? 'unknown',
      fileId: context?.fileId ?? null,
      bucket: context?.bucket ?? null,
      path: context?.path ?? null,
      urlPreview: typeof url === 'string' ? url.slice(0, 120) : String(url),
    });
    return { ok: false, error: UNSAFE_NAVIGATION_ERROR };
  }

  window.open(url!.trim(), '_blank', 'noopener,noreferrer');
  return { ok: true, url: url!.trim() };
}
